import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { connect } from "react-redux";
import { requestServerGet } from "../../actions";
import { IoIosFolderOpen, IoMdFolder } from "react-icons/io";
import { MdFolderShared, MdFolderSpecial } from "react-icons/md";
import InterDraggableListView from "./InterDraggableListView";
import MediaListElement from "../MediaListElement";
import MediaPreviewPopup from "../popups/MediaPreviewPopup";
import DataHelper from "../../helpers/DataHelper";
import { Tabs, Collapse, Pagination, Empty, Tooltip, Button, Grid } from "antd";
import {
  PlayCircleOutlined,
  LinkOutlined,
  FileImageOutlined,
  WifiOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import WidgetInstanceListElement from "../WidgetInstanceListElement";
import FolderBreadcrumb from "./FolderBreadcrumb";
import useFolderFileStore from "../../store/folderFileStore";
import { checkIsAPINew } from "../../Config";
import {
  useProLanguage,
  PRO_TRANSLATIONS,
} from "../../global/ProLanguageContext";

const { Panel } = Collapse;
const { TabPane } = Tabs;

function BrandFileListView(props) {
  const { lang } = useProLanguage();
  const t = PRO_TRANSLATIONS[lang].media;
  const screens = Grid.useBreakpoint();
  const isSmallScreen = !screens.md;

  const [fileDatas, setFileDatas] = useState([]);
  const [widgetInstanceDatas, setWidgetInstanceDatas] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(15);
  const [showAllFolders, setShowAllFolders] = useState(false);

  const folderFileDatas = useFolderFileStore((state) => state.folderFileDatas);
  const selectedFolderID = useFolderFileStore(
    (state) => state.selectedFolderID,
  );
  const setSelectedFolderID = useFolderFileStore(
    (state) => state.setSelectedFolderID,
  );
  const fetchFolderFileDatas = useFolderFileStore(
    (state) => state.fetchFolderFileDatas,
  );

  const mediaPreviewPopupRef = useRef();
  const folderContainerRef = useRef(null);
  const [foldersOverflow, setFoldersOverflow] = useState(false);

  const reloadFileDatas = useCallback(() => {
    const { selectedBrandData } = props;

    fetchFolderFileDatas(selectedBrandData, props);
    DataHelper.getFiles({ brandid: selectedBrandData.BrandID }, props).then(
      (data) => {
        setFileDatas(data);
      },
    );

    DataHelper.getWidgetInstances(
      { brandid: selectedBrandData.BrandID },
      props,
    ).then((data) => {
      setWidgetInstanceDatas(data);
    });
  }, [props]);

  useEffect(() => {
    reloadFileDatas();

    const { reloadFunctionMapper = () => {} } = props;
    reloadFunctionMapper(reloadFileDatas);
  }, [reloadFileDatas, props]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedFolderID]);

  useEffect(() => {
    // Reset page when filter changes
    // (same logic as class componentDidUpdate)
    setCurrentPage(1);
  }, [props.filterWord]);

  useEffect(() => {
    setShowAllFolders(false);
  }, [selectedFolderID]);

  const currentFolders = useMemo(() => {
    if (!checkIsAPINew()) return [];

    const { isBrandLevel, selectedBranchData, selectedBrandData } = props;

    return folderFileDatas
      .filter((f) =>
        selectedFolderID === null
          ? !f.FolderParentID ||
            f.FolderParentID === null ||
            f.FolderParentID === ""
          : f.FolderParentID === selectedFolderID,
      )
      .filter((f) => {
        if (isBrandLevel) return true;
        if (!selectedBranchData || selectedBrandData?.ContentSharingMode != "2")
          return true;

        const raw = f.AccessBranches;
        const isAll = !raw || raw === "null" || raw === "all" || raw === "";
        if (isAll) return true;

        const allowedIds = new Set(
          String(raw)
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
        );
        return allowedIds.has(String(selectedBranchData.BranchID));
      })
      .sort(
        (a, b) =>
          (a.CreateLevel || 0) - (b.CreateLevel || 0) ||
          a.FolderName.localeCompare(b.FolderName),
      );
  }, [folderFileDatas, selectedFolderID, props]);

  useEffect(() => {
    if (showAllFolders) return;
    const el = folderContainerRef.current;
    if (!el) return;
    const measure = () =>
      setFoldersOverflow(el.scrollHeight > el.clientHeight + 2);
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [currentFolders, showAllFolders, isSmallScreen]);

  const previewMedia = (data) => {
    if (data && mediaPreviewPopupRef.current) {
      mediaPreviewPopupRef.current.previewMedia(data);
    }
  };

  const renderMediaFiles = (type = "all") => {
    const { filterWord = "", selectedBrandData, disableDrag = false } = props;
    const { onQuickAddMedia = null } = props;

    const renderMediaItem = (data) => (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          paddingRight: 12,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <MediaListElement
            view="small"
            key={data.FileID}
            data={data}
            onPreviewClick={() => previewMedia(data)}
          />
        </div>
        {onQuickAddMedia && (
          <Tooltip title="Add to playlist">
            <Button
              shape="circle"
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onQuickAddMedia(data);
              }}
            />
          </Tooltip>
        )}
      </div>
    );

    let filteredItems = fileDatas.filter((file) => {
      if (type === "all") {
        return `${file.FileName}`
          .toLowerCase()
          .includes(filterWord.toLowerCase());
      }
      return (
        `${file.FileName}`.toLowerCase().includes(filterWord.toLowerCase()) &&
        file.FileType.toLowerCase() === type.toLowerCase()
      );
    });

    filteredItems = filteredItems
      .filter((data) => {
        if (!checkIsAPINew()) return true;
        if (!data.FolderParentID && selectedFolderID === null) return true;
        return (
          data.FolderParentID === selectedFolderID ||
          (selectedFolderID === null &&
            (data.FolderParentID === null || data.FolderParentID === ""))
        );
      })
      .sort((a, b) => b.FileID - a.FileID);

    const displayfilteredItems = filteredItems.filter((value, index) => {
      return (
        index < currentPage * pageSize && (currentPage - 1) * pageSize <= index
      );
    });

    const isEmpty = filteredItems.length === 0 && currentFolders.length === 0;

    return (
      <>
        {isEmpty && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ margin: "24px 0" }}
          />
        )}
        {filteredItems.length > 0 && (
          <Pagination
            style={{
              textAlign: "center",
              marginBottom: 8,
              marginTop: 6,
              fontSize: 12,
            }}
            responsive
            defaultPageSize={pageSize}
            onChange={(page) => setCurrentPage(page)}
            simple
            defaultCurrent={1}
            current={currentPage}
            total={filteredItems.length}
          />
        )}
        {disableDrag ? (
          <div>
            {displayfilteredItems.map((data) => (
              <div key={data.FileID}>{renderMediaItem(data)}</div>
            ))}
          </div>
        ) : (
          <InterDraggableListView
            shouldAcceptDrop={() => false}
            getChildPayload={(i) => ({
              ...displayfilteredItems[i],
              groupName: "file",
            })}
            groupName="file"
            items={displayfilteredItems}
            renderItem={(data) => renderMediaItem(data)}
          />
        )}
        {filteredItems.length > 0 && (
          <Pagination
            style={{ textAlign: "center" }}
            responsive
            defaultPageSize={pageSize}
            onChange={(page) => setCurrentPage(page)}
            simple
            defaultCurrent={1}
            current={currentPage}
            total={filteredItems.length}
          />
        )}
      </>
    );
  };

  const renderWidgets = (key) => {
    const { filterWord = "" } = props;
    const { onQuickAddWidget = null } = props;

    let filteredItems = widgetInstanceDatas.filter((widgetInstanceData) => {
      return widgetInstanceData.WidgetKey.toLowerCase() === key.toLowerCase();
    });

    filteredItems = filteredItems.sort((a, b) => b.FileID - a.FileID);

    return (
      <InterDraggableListView
        shouldAcceptDrop={() => false}
        getChildPayload={(i) => ({ ...filteredItems[i], groupName: "widget" })}
        groupName="widget"
        items={filteredItems}
        renderItem={(data) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              paddingRight: 12,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <WidgetInstanceListElement
                view="small"
                key={data.WidgetInstanceID}
                data={data}
                onPreviewClick={() => previewMedia(data)}
              />
            </div>
            {onQuickAddWidget && (
              <Tooltip title="Add to playlist">
                <Button
                  shape="circle"
                  size="small"
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onQuickAddWidget(data);
                  }}
                />
              </Tooltip>
            )}
          </div>
        )}
      />
    );
  };

  const getWidgetKeyObjects = () => {
    const widgetTypes = widgetInstanceDatas.reduce((prevData, currentData) => {
      const exist = prevData.some(
        (data) =>
          data.WidgetKey.toLowerCase() === currentData.WidgetKey.toLowerCase(),
      );

      if (exist) return prevData;

      prevData.push({
        WidgetName: currentData.WidgetName,
        WidgetKey: currentData.WidgetKey,
      });

      return prevData;
    }, []);

    return widgetTypes;
  };

  const widgetTypes = getWidgetKeyObjects();

  const pillHeight = isSmallScreen ? 28 : 32;
  const pillGap = 8;
  const collapsedMaxHeight = pillHeight;
  const shouldCollapseFolders = !isSmallScreen;
  const showToggleButton = shouldCollapseFolders && foldersOverflow;
  const { selectedBrandData } = props;

  return (
    <div style={{ height: "100%" }}>
      {checkIsAPINew() && (
        <div
          style={{
            padding: "8px 12px 6px",
            borderBottom: "1px solid #f1f5f9",
            marginBottom: 6,
          }}
        >
          <FolderBreadcrumb size="small" truncate={4} />
        </div>
      )}
      {currentFolders.length > 0 && (
        <div
          style={{
            padding: "4px 10px 10px",
            borderBottom: "1px solid #f1f5f9",
          }}
        >
          <div
            ref={folderContainerRef}
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: pillGap,
              overflow:
                shouldCollapseFolders && !showAllFolders ? "hidden" : "visible",
              maxHeight:
                shouldCollapseFolders && !showAllFolders
                  ? collapsedMaxHeight
                  : "none",
              transition: "max-height 0.2s ease",
            }}
          >
            {currentFolders.map((f) => {
              const isBrandLevelFolder =
                selectedBrandData?.ContentSharingMode == "2" &&
                f.CreateLevel < 2;
              const isRestrictedAccess =
                isBrandLevelFolder &&
                f.AccessBranches &&
                f.AccessBranches !== "null" &&
                f.AccessBranches !== "all" &&
                f.AccessBranches !== "";
              const isSelected =
                String(selectedFolderID) === String(f.FolderID);
              const iconColor = isSelected
                ? "#ffffff"
                : isBrandLevelFolder
                  ? "#6b9fd4"
                  : "#f5a623";
              const textColor = isSelected ? "#ffffff" : "#1e293b";
              const borderColor = isSelected
                ? isBrandLevelFolder
                  ? "#3b82f6"
                  : "#b45309"
                : isBrandLevelFolder
                  ? "#93c5fd"
                  : "#fcd34d";
              const bgColor = isSelected
                ? isBrandLevelFolder
                  ? "linear-gradient(135deg,#3b82f6,#2563eb)"
                  : "linear-gradient(135deg,#f59e0b,#d97706)"
                : isBrandLevelFolder
                  ? "linear-gradient(135deg,#eff6ff,#dbeafe)"
                  : "linear-gradient(135deg,#fff8db,#fef3c7)";
              const shadowColor = isSelected
                ? isBrandLevelFolder
                  ? "0 4px 10px rgba(59,130,246,0.28)"
                  : "0 4px 10px rgba(217,119,6,0.28)"
                : "none";
              return (
                <Tooltip
                  key={f.FolderID}
                  title={f.FolderName}
                  placement="top"
                  mouseEnterDelay={0.45}
                >
                  <div
                    onClick={() => setSelectedFolderID(f.FolderID)}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      height: pillHeight,
                      padding: isSmallScreen ? "0 8px" : "0 10px",
                      maxWidth: isSmallScreen ? 140 : 170,
                      borderRadius: 999,
                      cursor: "pointer",
                      border: `1px solid ${borderColor}`,
                      background: bgColor,
                      boxShadow: shadowColor,
                      transition: "all 0.15s",
                    }}
                  >
                    {isBrandLevelFolder ? (
                      isRestrictedAccess ? (
                        <MdFolderSpecial
                          style={{
                            fontSize: 14,
                            color: iconColor,
                            verticalAlign: "middle",
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <MdFolderShared
                          style={{
                            fontSize: 14,
                            color: iconColor,
                            verticalAlign: "middle",
                            flexShrink: 0,
                          }}
                        />
                      )
                    ) : (
                      <IoMdFolder
                        style={{
                          fontSize: 14,
                          color: iconColor,
                          verticalAlign: "middle",
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: isSmallScreen ? 11 : 12,
                        fontWeight: isSelected ? 600 : 500,
                        color: textColor,
                      }}
                    >
                      {f.FolderName}
                    </span>
                  </div>
                </Tooltip>
              );
            })}
          </div>
          {showToggleButton && (
            <Button
              type="link"
              size="small"
              onClick={() => setShowAllFolders((prev) => !prev)}
              style={{ marginTop: 2, padding: 0, height: "auto", fontSize: 12 }}
            >
              {showAllFolders ? "Show less" : "Show more"}
            </Button>
          )}
        </div>
      )}
      <Tabs centered onChange={() => setCurrentPage(1)}>
        <TabPane tab={t.panelMedias} key="medias">
          <Tabs
            defaultActiveKey="1"
            centered
            onChange={() => setCurrentPage(1)}
          >
            <TabPane tab={t.panelAll} key="1">
              {renderMediaFiles()}
            </TabPane>

            <TabPane
              tab={
                <FileImageOutlined
                  style={{
                    fontSize: "18px",
                    display: "inline-block",
                    textAlign: "center",
                    width: "20px",
                    margin: "0",
                  }}
                />
              }
              key="image"
            >
              {renderMediaFiles("image")}
            </TabPane>

            <TabPane
              tab={
                <PlayCircleOutlined
                  style={{
                    fontSize: "18px",
                    display: "inline-block",
                    textAlign: "center",
                    width: "20px",
                    margin: "0",
                  }}
                />
              }
              key="video"
            >
              {renderMediaFiles("video")}
            </TabPane>

            <TabPane
              tab={
                <LinkOutlined
                  style={{
                    fontSize: "18px",
                    display: "inline-block",
                    textAlign: "center",
                    width: "20px",
                    margin: "0",
                  }}
                />
              }
              key="web"
            >
              {renderMediaFiles("web")}
            </TabPane>

            <TabPane
              tab={
                <WifiOutlined
                  style={{
                    fontSize: "18px",
                    display: "inline-block",
                    textAlign: "center",
                    width: "20px",
                    margin: "0",
                  }}
                />
              }
              key="streamingurl"
            >
              {renderMediaFiles("streamingurl")}
            </TabPane>
          </Tabs>
        </TabPane>

        <TabPane tab={t.panelWidgets} key="widgets">
          <Collapse accordion>
            {widgetTypes.map((widgetType) => (
              <Panel header={widgetType.WidgetName} key={widgetType.WidgetKey}>
                {renderWidgets(widgetType.WidgetKey)}
              </Panel>
            ))}
          </Collapse>
        </TabPane>
      </Tabs>

      <MediaPreviewPopup ref={mediaPreviewPopupRef} />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    userdata: state.userdata,
    usertoken: state.usertoken,
    selectedBrandData: state.selectedBrandData,
    selectedBranchData: state.selectedBranchData,
  };
};

export default connect(mapStateToProps, { requestServerGet })(
  BrandFileListView,
);
